import { Autocomplete, styled } from '@mui/material';
import { useViewport } from 'contexts/viewport';
import { omit } from 'lodash';
import { cloneElement, createContext, forwardRef, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { VariableSizeList } from 'react-window';
import Box from 'shell/components/Box';
import IconButton from 'shell/components/IconButton';
import MenuList from 'shell/components/MenuList';
import Paper from 'shell/components/Paper';
import TextInput from 'shell/components/TextInput';
import { v4 as uuid } from 'uuid';

const CustomAutocomplete = styled(Autocomplete)(({ size }) => {
    const inputStyles = {
        small: {
            height: '28px',
            padding: '6px 12px',
        },
        medium: {
            height: '36px',
            padding: '10px 12px',
        },
        large: {
            height: '48px',
            padding: '14px 12px',
        },
    }[size];

    return {
        width: '100%',
        '& > .MuiFormControl-root': {
            paddingRight: '0 !important',
        },
        '& > .MuiFormControl-root > .MuiInputBase-root': {
            padding: '0 !important',
        },
        '& > .MuiFormControl-root > .MuiInputBase-root > .MuiInputBase-input': {
            ...inputStyles,
        },
    };
});

const CustomMenuList = styled(MenuList)(({ size, theme }) => {
    const fontStyles = {
        small: {
            minHeight: '1.5rem',
            fontSize: `${theme.typography.p3.fontSize.small}rem`,
            fontWeight: theme.typography.p3.fontWeight.regular,
            letterSpacing: `${theme.typography.p3.letterSpacing.small}rem`,
        },
        medium: {
            minHeight: '1.625rem',
            fontSize: `${theme.typography.p2.fontSize.large}rem`,
            fontWeight: theme.typography.p2.fontWeight.regular,
            letterSpacing: `${theme.typography.p2.letterSpacing.large}rem`,
        },
        large: {
            minHeight: '1.75rem',
            fontSize: `${theme.typography.p1.fontSize.large}rem`,
            fontWeight: theme.typography.p1.fontWeight.regular,
            letterSpacing: `${theme.typography.p1.letterSpacing.large}rem`,
        },
    }[size];

    return {
        maxHeight: '168px',
        overflowY: 'scroll',
        '& > li.MuiAutocomplete-option': {
            borderRadius: '4px',
            padding: theme.spacing(1, 2),
            flexShrink: 0,
            ...fontStyles,
            '&:hover': {
                background: theme.palette.ensightful.primary[50],
            },
            '&.Mui-focusVisible': {
                background: theme.palette.ensightful.primary[50],
                outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
                outlineOffset: '-5px',
            },
            '&.Mui-selected': {
                background: theme.palette.ensightful.primary[50],
            },
        },
    };
});

const CustomPaper = styled(Paper)(({ theme }) => {
    return {
        padding: 0,
        boxShadow: theme.palette.ensightful.shadows[1],
    };
});

const ExitIconButton = styled(IconButton)(() => {
    return {
        '&:focus-visible': {
            outlineWidth: '3px',
            outlineOffset: '-5px',
        },
        '& svg': {
            fontSize: '0.75rem',
        },
    };
});

const ArrowIconButton = styled(IconButton)(() => {
    return {
        '&:focus-visible': {
            outlineWidth: '3px',
            outlineOffset: '-5px',
        },
        '& svg': {
            fontSize: '1rem',
        },
    };
});

const CustomBox = styled(Box)(({ theme }) => {
    return {
        '& ul': {
            margin: 0,
        },
        '& li.MuiAutocomplete-option': {
            borderRadius: '4px',
            minHeight: '1.625rem',
            padding: theme.spacing(1, 2),
            fontSize: `${theme.typography.p2.fontSize.large}rem`,
            fontWeight: theme.typography.p2.fontWeight.regular,
            letterSpacing: `${theme.typography.p2.letterSpacing.large}rem`,
            '&:hover': {
                background: theme.palette.ensightful.primary[50],
            },
            '&.Mui-focusVisible': {
                background: theme.palette.ensightful.primary[50],
                outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
                outlineOffset: '-5px',
            },
            '&.Mui-selected': {
                background: theme.palette.ensightful.primary[50],
            },
        },
    };
});

const OuterElementContext = createContext({});

const OuterElementType = forwardRef((props, ref) => {
    const outerProps = useContext(OuterElementContext);
    return <Box ref={ref} {...props} {...outerProps} />;
});

const VirtualizedListboxComponent = forwardRef(({ children, ...rest }, ref) => {
    const virtualizedListRef = useRef({});
    const itemRefs = useRef({});

    const getHeight = () => {
        if (children.length > 6) return 168;
        return children.map((_, index) => getItemSize(index)).reduce((acc, current) => acc + current, 16);
    };

    const getItemSize = (index) => {
        return itemRefs.current[index] || 29;
    };

    const Row = ({ index, style }) => {
        const rowRef = useRef({});

        const inlineStyle = {
            ...style,
            top: style.top + 8,
            left: style.left + 8,
            width: 'calc(100% - 16px)',
        };

        useEffect(() => {
            if (rowRef.current) {
                virtualizedListRef.current.resetAfterIndex(0, true);
                itemRefs.current = { ...itemRefs.current, [index]: rowRef.current.clientHeight };
            }
        }, [index]);

        return <Box style={inlineStyle}>{cloneElement(children[index], { ref: rowRef })}</Box>;
    };

    return (
        <CustomBox ref={ref}>
            <OuterElementContext.Provider value={rest}>
                <VariableSizeList
                    ref={virtualizedListRef}
                    {...rest}
                    height={getHeight()}
                    innerElementType={'ul'}
                    itemCount={children.length}
                    itemSize={getItemSize}
                    outerElementType={OuterElementType}
                    width={'100%'}
                >
                    {Row}
                </VariableSizeList>
            </OuterElementContext.Provider>
        </CustomBox>
    );
});

export default forwardRef(
    ({ error, helperText, label, labelId, options, placeholder, required, virtualized, ...rest }, ref) => {
        const { device } = useViewport();
        const { t } = useTranslation();

        const mappedSize = {
            mobile: 'medium',
            tablet: 'medium',
            desktop: 'medium',
        }[device];

        if (rest.value) {
            rest.isOptionEqualToValue = (option, value) => {
                return option.key === value.key;
            };
        }

        const listboxId = uuid();

        return (
            <CustomAutocomplete
                ref={ref}
                {...rest}
                disableListWrap={virtualized}
                getOptionLabel={(option) => String(option.value)}
                ListboxComponent={forwardRef((props, ref) =>
                    virtualized ? (
                        <VirtualizedListboxComponent ref={ref} id={listboxId} {...props} />
                    ) : (
                        <CustomMenuList ref={ref} id={listboxId} {...props} size={mappedSize} />
                    )
                )}
                options={options}
                PaperComponent={CustomPaper}
                renderInput={({ InputProps, ...params }) => {
                    const restInputProps = omit(InputProps, ['endAdornment']);
                    params.InputLabelProps.color = 'primary';
                    params.InputLabelProps.shrink = true;
                    params.inputProps['aria-controls'] = listboxId;
                    params.inputProps['aria-invalid'] = undefined;
                    params.inputProps.invalid = Boolean(error);
                    return (
                        <TextInput
                            {...restInputProps}
                            {...params}
                            disablePointerEventsOnIcon={false}
                            endIcon={
                                <>
                                    {InputProps.endAdornment.props.children[0] ? (
                                        <>
                                            <ExitIconButton
                                                aria-label={'clear'}
                                                disabled={rest.disabled}
                                                kind={'exit'}
                                                size={mappedSize}
                                                variant={'square'}
                                                onClick={InputProps.endAdornment.props.children[0].props.onClick}
                                                onMouseDown={(e) => e.preventDefault()}
                                            />
                                            <ArrowIconButton
                                                aria-label={'toggle menu'}
                                                disabled={rest.disabled}
                                                kind={'dropdownArrowDown'}
                                                size={mappedSize}
                                                variant={'square'}
                                                onClick={InputProps.endAdornment.props.children[1].props.onClick}
                                                onMouseDown={(e) => e.preventDefault()}
                                            />
                                        </>
                                    ) : (
                                        <ArrowIconButton
                                            aria-label={'toggle menu'}
                                            disabled={rest.disabled}
                                            kind={'dropdownArrowDown'}
                                            size={mappedSize}
                                            variant={'square'}
                                            onClick={InputProps.endAdornment.props.children[1].props.onClick}
                                            onMouseDown={(e) => e.preventDefault()}
                                        />
                                    )}
                                </>
                            }
                            error={error}
                            helperText={helperText}
                            label={label}
                            labelId={labelId}
                            placeholder={placeholder}
                            required={required}
                        />
                    );
                }}
                size={mappedSize}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.stopPropagation();
                    }
                }}
                noOptionsText={t('ui.no-options', 'No options')}
            />
        );
    }
);
